/**
 * The owner's one ordered sender to its mirrors.
 *
 * Every event the owner relays carries a sequence number, and a mirror drops
 * anything numbered at or below what it has already applied
 * (`use-state-mirror.ts`). Tauri's `emit` is an asynchronous command, so two
 * sends started back to back can reach the mirrors in either order - and a
 * presentation frame, the largest thing relayed, was overtaken by the small
 * store mutation the owner sent a few milliseconds after it. Both mirrors then
 * dropped the frame without a word, the step never played, and the companion
 * waited 30 seconds for an acknowledgement that could not come (Windows live
 * run, 2026-09-30: once per form fill).
 *
 * So the number is given when the event is sent from here, and each send waits
 * for the one before it. The first goes out at once, as a bare `emit` did.
 */
import { emit } from "@tauri-apps/api/event";

import { nextOwnerSeq } from "@/lib/owner-seq";

let inFlight: Promise<void> | null = null;

/** Relay one event to the mirrors, numbered and in order. */
export function emitMirrorEvent(event: Record<string, unknown>): void {
  const payload = { ...event, seq: nextOwnerSeq() };
  // A failed send is not retried - a mirror that misses one resynchronises
  // from a snapshot - but it must not stop every send queued behind it.
  const send = () =>
    Promise.resolve(emit("denker://mirror-event", payload)).catch(() => undefined);
  const sent = inFlight ? inFlight.then(send) : send();
  inFlight = sent;
  void sent.then(() => {
    if (inFlight === sent) inFlight = null;
  });
}
